import fetch from "cross-fetch";
import { useEffect } from "react";
import { useQuery } from "react-query";
import settings from "src/settings";
import useClientContext, { setCurrentEntry } from "./useClientContext";

const { API_BACKEND_HOSTNAME } = settings;

/**
 * Hook that uses react-query to interface with the user session API store (sso.json).
 * @example
 * import useSession from "@hooks/useSession";
 * ...
 * const { data: session } = useSession();
 * const name = session.user.firstName
 */
export default function useSession() {
  const [{ gigyaProfile, currentEntry }, dispatch] = useClientContext();

  useEffect(() => {
    if (currentEntry && gigyaProfile === null) {
      dispatch(setCurrentEntry(null));
    }
  }, [gigyaProfile, currentEntry, dispatch]);

  return useQuery<ISession, Error>(
    "session",
    () =>
      fetch(`${API_BACKEND_HOSTNAME}/api/sso.json`, {
        headers: { "content-type": "application/json" },
        method: "POST",
        body: JSON.stringify(gigyaProfile),
      }).then((res) => res.json()),
    {
      enabled: Boolean(gigyaProfile),
      cacheTime: null,
      refetchInterval: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      initialData: {
        entries: {
          chase: [],
        },
        user: {
          token: null,
        },
        errors: 0,
      },
      onSuccess: (data) => {
        // set client's current entry
        const firstEntry = data.entries?.chase?.[0] ?? null;
        if (!currentEntry && firstEntry) {
          dispatch(setCurrentEntry(firstEntry));
        }
      },
    },
  );
}

export interface ISession {
  entries: {
    chase?: PlayoffsEntry[];
  };
  user: {
    id?: number;
    first_name?: string;
    last_name?: string;
    email?: string;
    is_admin?: false;
    created_at?: string;
    updated_at?: string;
    nascar_id?: string;
    last_login_at?: string;
    http_user_agent?: string;
    display_name?: string;
    favorite_driver?: any | null;
    birthday?: any | null;
    gender?: any | null;
    live_entries_count?: number;
    streak_entries_count?: number;
    forem_admin?: false;
    forem_state?: string;
    forem_auto_subscribe?: false;
    forum_banned_at?: any | null;
    faceoff_entries_count?: number;
    returned_at?: any | null;
    chase_entries_count?: number;
    is_mobile?: false;
    props_entries_count?: number;
    fantasy_notifications?: false;
    token: string;
  };
  errors: number;
}

export interface PlayoffsEntry {
  id: number;
  avatar_id: number;
  name: string;
  entry_bar: {
    league_ranks: unknown;
    points: {
      percentile: string;
      rank: string;
      round_1: string;
      round_2: string;
      round_3: string;
      round_4: string;
      total_points: string;
    };
  };
  picks: {
    1: [
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
    ];
    2: [
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
    ];
    3: [
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
      IPick | ILeagueViewPick,
    ];
    4: [IPick | ILeagueViewPick];
  };
  results: {
    rounds: [Round, Round, Round, Round];
  };
  send_emails: boolean;
  user_id: number;
  token?: string;
  league?: ILeague;
}

export type IPick = number | null;

// This is used only for when a user is viewing another users picks via the League User view, we pass the points and the driver ID instead of just the driver ID.
export interface ILeagueViewPick {
  driver_id: number;
  points: number;
}

interface Round {
  bonus_points: IPick[];
  points: IPick[];
  total_points: IPick[];
}

interface ILeague {
  closed: boolean;
  id: number;
  is_garage_hidden: boolean;
  is_single_race: boolean;
  is_sponsored: boolean;
  name: string;
  playoffs_points: string;
  playoffs_rank: string;
  points: number;
  prize_eligible: true;
  race_points: number;
  race_rank: number;
  rank: number;
  regular_season_points: number;
  regular_season_rank: number;
  slug: string;
}
